import React, { useState, useRef, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { Steps } from "primereact/steps";
import { FileUpload } from "primereact/fileupload";
import { useDispatch, useSelector } from "react-redux";
import { createClient } from "../../service/client/ClientServices";
import {
  ClientAddressDto,
  ContactPersonDto,
  ClientDocumentDto,
} from "../../types/client.types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faDownload } from "@fortawesome/free-solid-svg-icons";
import { getAllPaymentTerms } from "../../service/paymentTerms/PaymentTermsService";
import { getAllBusinessTypes } from "../../service/businessType/BusinessTypeService";
import { getAllIndustries } from "../../service/industry/IndustryServices";
import { 
  faUser, 
  faMapMarkerAlt, 
  faAddressBook, 
  faCog, 
  faFileAlt 
} from "@fortawesome/free-solid-svg-icons";

interface ClientDialogProps {
  visible: boolean;
  onHide: () => void;
  onClientCreated: (client: any) => void;
  countries: { label: string; value: number }[];
}

interface ClientDataType {
  client_name: string;
  primary_contact_person?: string;
  company_name?: string;
  email?: string;
  website_url?: string;
  phone?: string;
  currency_code?: string;
  business_type_id: number;
  industry_id?: number;
  payment_term_id: number;
  pan_no?: string;
  billing_address?: ClientAddressDto;
  shipping_address?: ClientAddressDto;
  same_as_billing?: boolean;
  contact_persons?: ContactPersonDto[];
  documents?: ClientDocumentDto[];
  tax_number?: string;
  notes?: string;
}

export const ClientDialog: React.FC<ClientDialogProps> = ({
  visible,
  onHide,
  onClientCreated,
  countries,
}) => {
  const dispatch = useDispatch();
  const localization = useSelector((state: any) => state.localization);
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [paymentTerms, setPaymentTerms] = useState<
    { label: string; value: number }[]
  >([]);
  const [businessTypes, setBusinessTypes] = useState<
    { label: string; value: number }[]
  >([]);
  const [industryTypes, setIndustryTypes] = useState<
    { label: string; value: number }[]
  >([]);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [clientData, setClientData] = useState<ClientDataType>({
    client_name: "",
    primary_contact_person: "",
    company_name: "",
    email: "",
    phone: "",
    currency_code: "",
    industry_id: 0,
    business_type_id: 0,
    payment_term_id: 0,
    pan_no: "",
    website_url: "",
    billing_address: {
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      country_id: undefined,
    },
    shipping_address: {
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      country_id: undefined,
    },
    same_as_billing: false,
    contact_persons: [],
    documents: [],
    tax_number: "",
    notes: "",
  });

  // Define steps for the wizard
const steps = [
  { 
    label: localization?.GeneralInformation || "General Information",
    command: () => setActiveIndex(0),
    template: (item:any, options:any) => {
      return (
        <span className={options.className} onClick={options.onClick} style={{cursor:'pointer'}}>
          <span className="p-steps-number">
            <FontAwesomeIcon icon={faUser} />
          </span>
          <span className="p-steps-title">{item.label}</span>
        </span>
      );
    }
  },
  { 
    label: localization?.Addresses || "Addresses",
    command: () => setActiveIndex(1),
    template: (item:any, options:any) => {
      return (
        <span className={options.className} onClick={options.onClick} style={{cursor:'pointer'}}>
          <span className="p-steps-number">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
          </span>
          <span className="p-steps-title">{item.label}</span>
        </span>
      );
    }
  },
  { 
    label: localization?.ContactPersons || "Contact Persons",
    command: () => setActiveIndex(2),
    template: (item:any, options:any) => {
      return (
        <span className={options.className} onClick={options.onClick} style={{cursor:'pointer'}}>
          <span className="p-steps-number">
            <FontAwesomeIcon icon={faAddressBook} />
          </span>
          <span className="p-steps-title">{item.label}</span>
        </span>
      );
    }
  },
  { 
    label: localization?.ClientSettings || "Client Settings",
    command: () => setActiveIndex(3),
    template: (item:any, options:any) => {
      return (
        <span className={options.className} onClick={options.onClick} style={{cursor:'pointer'}}>
          <span className="p-steps-number">
            <FontAwesomeIcon icon={faCog} />
          </span>
          <span className="p-steps-title">{item.label}</span>
        </span>
      );
    }
  },
  { 
    label: localization?.Documents || "Documents",
    command: () => setActiveIndex(4),
    template: (item:any, options:any) => {
      return (
        <span className={options.className} onClick={options.onClick} style={{cursor:'pointer'}}>
          <span className="p-steps-number">
            <FontAwesomeIcon icon={faFileAlt} />
          </span>
          <span className="p-steps-title">{item.label}</span>
        </span>
      );
    }
  }
];

// Create a template function for the Steps component

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setClientData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for the field if it exists
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleAddressChange = (
    type: "billing" | "shipping",
    field: string,
    value: any
  ) => {
    const addressKey = `${type}_address`;
    setClientData((prev) => {
      const updated = { ...prev };
      if (!updated[`${type}_address`]) {
        updated[`${type}_address`] = {} as ClientAddressDto;
      }

      (updated[`${type}_address`] as any)[field] = value;

      if (type === "billing" && clientData.same_as_billing) {
        if (!updated.shipping_address) {
          updated.shipping_address = {} as ClientAddressDto;
        }
        (updated.shipping_address as any)[field] = value;
      }

      return updated;
    });

    // Clear error if valid
    if (field === "city" && value.trim()) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[`${addressKey}.city`];
        return newErrors;
      });
    }
  };

  const handleSameAsBillingChange = (checked: boolean) => {
    setClientData((prev) => {
      const updated = { ...prev, same_as_billing: checked };

      if (checked) {
        updated.shipping_address = { ...prev.billing_address };
      }

      return updated;
    });
  };

  const addContactPerson = () => {
    setClientData((prev) => ({
      ...prev,
      contact_persons: [
        ...(prev.contact_persons || []),
        { first_name: "", last_name: "", email: "", phone: "" },
      ],
    }));
  };

  const updateContactPerson = (index: number, field: string, value: string) => {
    setClientData((prev) => {
      const updatedContactPersons = [...(prev.contact_persons || [])];
      if (!updatedContactPersons[index]) {
        updatedContactPersons[index] = {
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
        };
      }

      updatedContactPersons[index] = {
        ...updatedContactPersons[index],
        [field]: value,
      };

      return {
        ...prev,
        contact_persons: updatedContactPersons,
      };
    });
  };

  const removeContactPerson = (index: number) => {
    setClientData((prev) => {
      const updatedContactPersons = [...(prev.contact_persons || [])];
      updatedContactPersons.splice(index, 1);

      return {
        ...prev,
        contact_persons: updatedContactPersons,
      };
    });
  };

  const onFileUpload = (event: any) => {
    const files = event.files;
    const newUploadedFiles = [...uploadedFiles];

    for (let file of files) {
      const filePath = `/uploads/${Date.now()}_${file.name}`;
      newUploadedFiles.push({
        name: file.name,
        path: filePath,
        size: file.size,
        type: file.type,
      });

      setClientData((prev) => ({
        ...prev,
        documents: [
          ...(prev.documents || []),
          {
            file_path: filePath,
            file_name: file.name,
            file_type: file.type,
            file_size: file.size,
          },
        ],
      }));
    }

    setUploadedFiles(newUploadedFiles);
    fileUploadRef.current.clear();

    toast.current?.show({
      severity: "success",
      summary: localization?.Success || "Success",
      detail: localization?.FileUploadedSuccessfully || "File(s) uploaded",
    });
  };

  const removeFile = (index: number) => {
    const newUploadedFiles = [...uploadedFiles];
    const removedFile = newUploadedFiles.splice(index, 1)[0];

    setUploadedFiles(newUploadedFiles);

    setClientData((prev) => ({
      ...prev,
      documents: (prev.documents || []).filter(
        (doc) => doc.file_path !== removedFile.path
      ),
    }));
  };

  const handleSubmit = async () => {
    // Validate form
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const errorFields = Object.keys(validationErrors)
        .map((key) => {
          switch (key) {
            case "payment_term_id":
              return localization?.PaymentTerms || "Payment term";
            case "billing_address.city":
              return localization?.City || "Billing address (city)";
            case "business_type_id":
              return localization?.BusinessTypeRequired || "Business type";
            case "currency_code":
              return localization?.CurrencyRequired || "Currency";
            case "client_name":
              return localization?.ClientNameIsRequired || "Client name";
            case "primary_contact_person":
              return (
                localization?.PrimaryContactPersonRequired ||
                "Primary contact person"
              );
            case "email":
              return localization?.EmailRequired || "Email";
            case "phone":
              return localization?.PhoneNumberRequired || "Phone number";
            default:
              if (key.startsWith("contact_person_")) return null; // Skip individual contact person fields
              return null;
          }
        })
        .filter(Boolean); // Remove nulls

      const detailMessage =
        errorFields.length > 0
          ? `${
              localization?.PleaseFixFollowingFields ||
              "Please fix the following fields"
            }: ${errorFields.join(", ")}`
          : localization?.PleaseFillinAllRequiredFields ||
            "Please fix the errors in the form";

      toast.current?.show({
        severity: "error",
        summary: localization?.Error || "Error",
        detail: detailMessage,
      });

      return;
    }

    setIsSubmitting(true);

    try {
      const response = await createClient(dispatch, clientData);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: localization?.Success || "Success",
          detail:
            localization?.ClientCreatedSuccessfully ||
            "Client created successfully",
          life: 3000,
        });

        const createdClient = {
          id: response?.client?.client_id,
          name: clientData.client_name,
          email: clientData.email,
          contactPerson: clientData.primary_contact_person,
          company_name: clientData.company_name,
          website_url: clientData?.website_url,
          phone: clientData.phone,
          currency_code: clientData.currency_code,
          industry_id: clientData.industry_id,
          business_type_id: clientData.business_type_id,
          business_type: clientData.business_type_id,
          payment_term_id: clientData.payment_term_id,
          payment_terms: clientData.payment_term_id,
          pan_no: clientData.pan_no,
          billing_address: clientData.billing_address,
          shipping_address: clientData.shipping_address,
          contact_persons: clientData.contact_persons,
          documents: clientData.documents,
          tax_number: clientData.tax_number,
          notes: clientData.notes,
        };

        onClientCreated(createdClient);
        onHide();
        setErrors({});
      } else {
        toast.current?.show({
          severity: "error",
          summary: localization?.Error || "Error",
          detail:
            localization?.FailedToCreateClient || "Failed to create client",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error creating client:", error);
      toast.current?.show({
        severity: "error",
        summary: localization?.Error || "Error",
        detail:
          localization?.FailedToCreateClientTryAgain ||
          "Failed to create client. Please try again.",
        life: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setClientData({
      client_name: "",
      primary_contact_person: "",
      company_name: "",
      email: "",
      phone: "",
      currency_code: "",
      business_type_id: 0,
      payment_term_id: 0,
      pan_no: "",
      billing_address: {
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        postal_code: "",
        country_id: undefined,
      },
      shipping_address: {
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        postal_code: "",
        country_id: undefined,
      },
      same_as_billing: false,
      contact_persons: [],
      documents: [],
      tax_number: "",
      notes: "",
    });
    setUploadedFiles([]);
    setActiveIndex(0);
  };
  
  // Debounce effect when PAN number changes
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      checkPanNumberValidity();
    }, 500); // 500ms delay

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [clientData.pan_no]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Fetch payment terms
        const paymentTermsResponse = await getAllPaymentTerms(dispatch);
        if (paymentTermsResponse) {
          setPaymentTerms(
            paymentTermsResponse.map((term: any) => ({
              label: term.name,
              value: term.payment_term_id,
            }))
          );
        }

        // Fetch business types
        const businessTypesResponse = await getAllBusinessTypes(dispatch);
        if (businessTypesResponse) {
          setBusinessTypes(
            businessTypesResponse.map((type: any) => ({
              label: type.name,
              value: type.business_type_id,
              description:type.description
            }))
          );
        }
        // Fetch industry types
        const industryTypesResponse = await getAllIndustries(dispatch, {
          Page: 1,
          Limit: 100,
          SortBy: "ASC",
          SortProperty: "industry_name",
        });
        if (industryTypesResponse) {
          setIndustryTypes(
            industryTypesResponse?.List?.map((type: any) => ({
              label: type.industry_name,
              value: type.industry_id,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.current?.show({
          severity: "error",
          summary: localization?.Error || "Error",
          detail: localization?.FailedToLoadData || "Failed to load data",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [dispatch]);

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  const handleNext = () => {
    setActiveIndex((prev) => prev + 1);
  };

  const renderFooter = () => {
    return (
      <div className="flex justify-end space-x-2">
        <Button
          label={localization?.Cancel || "Cancel"}
          icon="pi pi-times"
          className="p-button-outlined"
          onClick={() => {
            onHide();
            setErrors({});
          }}
          disabled={isSubmitting}
        />
        {activeIndex < 4 ? (
          <Button
            label={localization?.Next || "Next"}
            icon="pi pi-check"
            className="bg-gold text-white"
            onClick={handleNext}
          ></Button>
        ) : (
          <Button
            label={localization?.CreateClient || "Create Client"}
            icon="pi pi-check"
            className="bg-gold text-white"
            onClick={handleSubmit}
            loading={isSubmitting}
          />
        )}
      </div>
    );
  };

  const checkPanNumberValidity = () => {
    if (
      clientData.pan_no?.trim() &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(clientData.pan_no)
    ) {
      setErrors((prev) => ({
        ...prev,
        pan_no: "Please enter a valid PAN number (e.g., ABCDE1234F)",
      }));
    } else {
      setErrors((prev) => ({ ...prev, pan_no: "" }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!clientData.client_name?.trim()) {
      newErrors.client_name =
        localization?.ClientNameIsRequired || "Client name is required";
    }

    if (!clientData.primary_contact_person?.trim()) {
      newErrors.primary_contact_person =
        localization?.PrimaryContactPersonRequired ||
        "Primary contact person is required";
    }

    if (!clientData.email?.trim()) {
      newErrors.email = localization?.EmailRequired || "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(clientData.email)) {
      newErrors.email =
        localization?.PleaseEnterValidEmail ||
        "Please enter a valid email address";
    }

    // Phone number validation
    if (!clientData.phone?.trim()) {
      newErrors.phone =
        localization?.PhoneNumberRequired || "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(clientData.phone)) {
      newErrors.phone =
        localization?.PleaseEnterValid10DigitPhone ||
        "Please enter a valid 10-digit phone number";
    }

    // Validate contact persons
    if (clientData.contact_persons && clientData.contact_persons.length > 0) {
      clientData.contact_persons.forEach((person, index) => {
        if (!person.email) {
          newErrors[`contact_person_${index}_email`] =
            localization?.EmailRequired ||
            "Email is required for contact person";
        } else if (!/^\S+@\S+\.\S+$/.test(person.email)) {
          newErrors[`contact_person_${index}_email`] =
            localization?.PleaseEnterValidEmail ||
            "Please enter a valid email address";
        }

        if (!person.first_name) {
          newErrors[`contact_person_${index}_first_name`] =
            localization?.FirstName ||
            "First name is required for contact person";
        }

        if (!person.last_name) {
          newErrors[`contact_person_${index}_last_name`] =
            localization?.LastName ||
            "Last name is required for contact person";
        }
      });
    }

    // Validate billing address
    if (!clientData.billing_address?.city?.trim()) {
      newErrors["billing_address.city"] =
        localization?.City || "City is required for billing address";
    }

    // Validate payment term
    if (!clientData.payment_term_id) {
      newErrors.payment_term_id =
        localization?.PaymentTerms || "Payment term is required";
    }

    // Validate business type
    if (!clientData.business_type_id) {
      newErrors.business_type_id =
        localization?.BusinessTypeRequired || "Business type is required";
    }
    // Validate business type
    if (!clientData.currency_code) {
      newErrors.currency_code =
        localization?.CurrencyRequired || "Currency is required";
    }

    return newErrors;
  };

  // Function to render content based on the active step
  const renderStepContent = () => {
    switch (activeIndex) {
      case 0:
        return (
          // General Information
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div className="col-span-1 md:col-span-2">
              <label
                htmlFor="client_name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {`${localization?.ClientName || "Client Name"} *`}
              </label>
              <InputText
                id="client_name"
                name="client_name"
                value={clientData.client_name}
                onChange={handleInputChange}
                className="w-full"
                required
              />
              {errors.client_name && (
                <small className="p-error">{errors.client_name}</small>
              )}
            </div>

            <div>
              <label
                htmlFor="primary_contact_person"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {`${
                  localization?.PrimaryContactPerson ||
                  "Primary Contact Person"
                } *`}
              </label>
              <InputText
                id="primary_contact_person"
                name="primary_contact_person"
                value={clientData.primary_contact_person}
                onChange={handleInputChange}
                className="w-full"
              />
              {errors.primary_contact_person && (
                <small className="p-error">
                  {errors.primary_contact_person}
                </small>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {`${localization?.Email || "Email"} *`}
              </label>
              <InputText
                id="email"
                name="email"
                value={clientData.email}
                onChange={handleInputChange}
                className="w-full"
              />
              {errors.email && (
                <small className="p-error">{errors.email}</small>
              )}
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {`${localization?.Phone || "Phone"} *`}
              </label>
              <InputText
                id="phone"
                name="phone"
                value={clientData.phone}
                onChange={handleInputChange}
                className="w-full"
              />
              {errors.phone && (
                <small className="p-error">{errors.phone}</small>
              )}
            </div>

            <div>
              <label
                htmlFor="pan_no"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.PANTaxNumber || "PAN/Tax Number"}
              </label>
              <InputText
                id="pan_no"
                name="pan_no"
                value={clientData.pan_no}
                onChange={handleInputChange}
                className="w-full"
              />
              {errors.pan_no && (
                <small className="p-error">{errors.pan_no}</small>
              )}
            </div>

            <div>
              <label
                htmlFor="website_url"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.WebsiteUrl || "Website Url"}
              </label>
              <InputText
                id="website_url"
                name="website_url"
                value={clientData.website_url}
                onChange={handleInputChange}
                className="w-full"
              />
            </div>

            <div>
              <label
                htmlFor="currency_code"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {`${localization?.Currency || "Currency"} *`}
              </label>
              <Dropdown
                id="currency_code"
                name="currency_code"
                value={clientData.currency_code}
                options={[
                  { label: "USD - US Dollar", value: "USD" },
                  { label: "EUR - Euro", value: "EUR" },
                  { label: "GBP - British Pound", value: "GBP" },
                  { label: "INR - Indian Rupee", value: "INR" },
                ]}
                onChange={(e) => {
                  setClientData((prev) => ({
                    ...prev,
                    currency_code: e.value,
                  }));
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors["currency_code"];
                    return newErrors;
                  });
                }}
                placeholder={
                  localization?.SelectCurrency || "Select Currency"
                }
                className="w-full"
              />
              {errors.currency_code && (
                <small className="p-error">{errors.currency_code}</small>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {`${localization?.BusinessType || "GST Treatment"} *`}
              </label>
              <Dropdown
                value={clientData.business_type_id}
                options={businessTypes}
                onChange={(e) => {
                  const selectedBusinessType = businessTypes?.find((typ)=>typ.value===e.value)
                  setClientData((prev) => ({
                    ...prev,
                    business_type_id: e.value,
                    business_type:selectedBusinessType?.label
                  }));
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors["business_type_id"];
                    return newErrors;
                  });
                }}
                placeholder={
                  localization?.SelectBusinessType || "Select GST Treatment"
                }
                className={`w-full ${
                  errors.business_type_id ? "p-invalid" : ""
                }`}

                 itemTemplate={(option: any) => (
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm">{option.label}</span>
                      <span className="text-xs text-gray-500 whitespace-normal break-words w-full">
                        {option.description}
                      </span>
                    </div>
                  )}
                  valueTemplate={(option: any) =>
                    option ? (
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">{option.label}</span>
                        <span className="text-xs text-gray-500 whitespace-normal break-words w-full">
                          {option.description}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">
                        {localization?.SelectBusinessType || "Select GST Treatment"}
                      </span>
                    )
                  }
                  panelClassName="w-[250px]" // 👈 controls the dropdown list width
              />
              {errors.business_type_id && (
                <small className="p-error">{errors.business_type_id}</small>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {localization?.IndustryType || "Industry Type"}
              </label>
              <Dropdown
                value={clientData.industry_id}
                options={industryTypes}
                onChange={(e) => {
                  setClientData((prev) => ({
                    ...prev,
                    industry_id: e.value,
                  }));
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors["industry_id"];
                    return newErrors;
                  });
                }}
                placeholder={
                  localization?.SelectIndustryType || "Select Industry Type"
                }
                className={`w-full ${errors.industry_id ? "p-invalid" : ""}`}
              />
              {errors.industry_id && (
                <small className="p-error">{errors.industry_id}</small>
              )}
            </div>

            <div className="col-span-1 md:col-span-2">
              <label
                htmlFor="notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.Notes || "Notes"}
              </label>
              <InputTextarea
                id="notes"
                name="notes"
                value={clientData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full"
              />
            </div>
          </div>
        );
      case 1:
        return (
          // Addresses
          <div className="space-y-4 mt-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {localization?.BillingAddress || "Billing Address"}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="col-span-1 md:col-span-2">
                  <label
                    htmlFor="billing_address_line1"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {localization?.AddressLine1 || "Address Line 1"}
                  </label>
                  <InputText
                    id="billing_address_line1"
                    value={clientData.billing_address?.address_line1 || ""}
                    onChange={(e) =>
                      handleAddressChange(
                        "billing",
                        "address_line1",
                        e.target.value
                      )
                    }
                    className="w-full"
                  />
                </div>

                <div className="col-span-1 md:col-span-2">
                  <label
                    htmlFor="billing_address_line2"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {localization?.AddressLine2 || "Address Line 2"}
                  </label>
                  <InputText
                    id="billing_address_line2"
                    value={clientData.billing_address?.address_line2 || ""}
                    onChange={(e) =>
                      handleAddressChange(
                        "billing",
                        "address_line2",
                        e.target.value
                      )
                    }
                    className="w-full"
                  />
                </div>

                <div>
                  <label
                    htmlFor="billing_city"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {`${localization?.City || "City"}  *`}
                  </label>
                  <InputText
                    id="billing_city"
                    value={clientData.billing_address?.city || ""}
                    onChange={(e) =>
                      handleAddressChange("billing", "city", e.target.value)
                    }
                    className={`w-full ${
                      errors["billing_address.city"] ? "p-invalid" : ""
                    }`}
                  />
                  {errors["billing_address.city"] && (
                    <small className="p-error">
                      {errors["billing_address.city"]}
                    </small>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="billing_state"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {localization?.StateProvince || "State/Province"}
                  </label>
                  <InputText
                    id="billing_state"
                    value={clientData.billing_address?.state || ""}
                    onChange={(e) =>
                      handleAddressChange("billing", "state", e.target.value)
                    }
                    className="w-full"
                  />
                </div>

                <div>
                  <label
                    htmlFor="billing_postal_code"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {localization?.PostalCode || "Postal Code"}
                  </label>
                  <InputText
                    id="billing_postal_code"
                    value={clientData.billing_address?.postal_code || ""}
                    onChange={(e) =>
                      handleAddressChange(
                        "billing",
                        "postal_code",
                        e.target.value
                      )
                    }
                    className="w-full"
                  />
                </div>

                <div>
                  <label
                    htmlFor="billing_country"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {localization?.Country || "Country"}
                  </label>
                  <Dropdown
                    id="billing_country"
                    value={clientData.billing_address?.country_id}
                    options={countries}
                    onChange={(e) =>
                      handleAddressChange("billing", "country_id", e.value)
                    }
                    placeholder={
                      localization?.SelectCountry || "Select Country"
                    }
                    className="w-full"
                    optionLabel="label"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center my-3">
              <Checkbox
                checked={clientData.same_as_billing as boolean}
                onChange={(e) =>
                  handleSameAsBillingChange(e.checked || false)
                }
                className="mr-2"
              />
              <label className="text-sm font-medium text-gray-700">
                {localization?.ShippingAddressSameAsBilling ||
                  "Shipping address same as billing"}
              </label>
            </div>

            {!clientData.same_as_billing && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {localization?.ShippingAddress || "Shipping Address"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="col-span-1 md:col-span-2">
                    <label
                      htmlFor="shipping_address_line1"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {localization?.AddressLine1 || "Address Line 1"}
                    </label>
                    <InputText
                      id="shipping_address_line1"
                      value={clientData.shipping_address?.address_line1 || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "address_line1",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <label
                      htmlFor="shipping_address_line2"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {localization?.AddressLine2 || "Address Line 2"}
                    </label>
                    <InputText
                      id="shipping_address_line2"
                      value={clientData.shipping_address?.address_line2 || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "address_line2",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="shipping_city"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {localization?.City || "City"}
                    </label>
                    <InputText
                      id="shipping_city"
                      value={clientData.shipping_address?.city || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "city",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="shipping_state"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {localization?.StateProvince || "State/Province"}
                    </label>
                    <InputText
                      id="shipping_state"
                      value={clientData.shipping_address?.state || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "state",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="shipping_postal_code"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {localization?.PostalCode || "Postal Code"}
                    </label>
                    <InputText
                      id="shipping_postal_code"
                      value={clientData.shipping_address?.postal_code || ""}
                      onChange={(e) =>
                        handleAddressChange(
                          "shipping",
                          "postal_code",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="shipping_country"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {localization?.Country || "Country"}
                    </label>
                    <Dropdown
                      id="shipping_country"
                      value={clientData.shipping_address?.country_id}
                      options={countries}
                      onChange={(e) =>
                        handleAddressChange("shipping", "country_id", e.value)
                      }
                      placeholder={
                        localization?.SelectCountry || "Select Country"
                      }
                      className="w-full"
                      optionLabel="label"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 2:
        return (
          // Contact Persons
          <div className="mt-3">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">
                {localization?.ContactPersons || "Contact Persons"}
              </h3>
              <Button
                type="button"
                onClick={addContactPerson}
                className="bg-primary-600 hover:bg-primary-700"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {localization?.AddContactPerson || "Add Contact Person"}
              </Button>
            </div>

            {clientData.contact_persons &&
            clientData.contact_persons.length > 0 ? (
              <div className="space-y-4">
                {clientData.contact_persons.map((person, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-md p-3 relative"
                  >
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="absolute top-2 right-2 p-button-danger p-button-text p-button-sm w-12 mb-4"
                      onClick={() => removeContactPerson(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {localization?.FirstName || "First Name *"}
                        </label>
                        <InputText
                          value={person.first_name || ""}
                          onChange={(e) =>
                            updateContactPerson(
                              index,
                              "first_name",
                              e.target.value
                            )
                          }
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {localization?.LastName || "Last Name *"}
                        </label>
                        <InputText
                          value={person.last_name || ""}
                          onChange={(e) =>
                            updateContactPerson(
                              index,
                              "last_name",
                              e.target.value
                            )
                          }
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {localization?.Email || "Email *"}
                        </label>
                        <InputText
                          value={person.email || ""}
                          onChange={(e) =>
                            updateContactPerson(
                              index,
                              "email",
                              e.target.value
                            )
                          }
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {localization?.Phone || "Phone"}
                        </label>
                        <InputText
                          value={person.phone || ""}
                          onChange={(e) =>
                            updateContactPerson(
                              index,
                              "phone",
                              e.target.value
                            )
                          }
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-center py-6">
                {localization?.NoContactPersonsAdded ||
                  "No contact persons added. Click the button above to add a contact person."}
              </div>
            )}
          </div>
        );
      case 3:
        return (
          // Client Settings
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {localization?.PaymentTerms || "Payment Terms *"}
            </label>
            <Dropdown
              value={clientData.payment_term_id}
              options={paymentTerms}
              onChange={(e) => {
                setClientData((prev) => ({
                  ...prev,
                  payment_term_id: e.value,
                  payment_terms: e.value,
                }));
                setErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors["payment_term_id"];
                  return newErrors;
                });
              }}
              placeholder={
                localization?.SelectPaymentTerms || "Select Payment Term"
              }
              className={`w-full ${
                errors.payment_term_id ? "p-invalid" : ""
              }`}
            />
            {errors.payment_term_id && (
              <small className="p-error">{errors.payment_term_id}</small>
            )}
          </div>
        );
      case 4:
        return (
          // Documents
          <div className="mt-3">
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">
                {localization?.UploadDocuments || "Upload Documents"}
              </h3>
              <FileUpload
                ref={fileUploadRef}
                multiple
                customUpload
                uploadHandler={onFileUpload}
                accept="image/*,application/pdf"
                maxFileSize={1000000}
                emptyTemplate={
                  <p className="m-0">
                    {localization?.DragAndDropFilesHereToUpload ||
                      "Drag and drop files here to upload."}
                  </p>
                }
              />
            </div>

            <div className="mt-4">
              <h3 className="text-lg font-medium mb-2">
                {localization?.UploadedDocuments || "Uploaded Documents"}
              </h3>
              {uploadedFiles.length > 0 ? (
                <div className="space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                    >
                      <span className="flex items-center">
                        <FontAwesomeIcon
                          icon={
                            file.type.includes("image")
                              ? faDownload
                              : faDownload
                          }
                          className="text-gray-500 mr-2"
                        />
                        {file.name}
                      </span>
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-danger p-button-text"
                        onClick={() => removeFile(index)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-center py-4">
                  {localization?.NoDocumentsUploaded ||
                    "No documents uploaded."}
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header={localization?.CreateNewClient || "Create New Client"}
        visible={visible}
        onHide={() => {
          onHide();
          setErrors({});
        }}
        footer={renderFooter()}
        style={{ width: "90%", maxWidth: "850px" }}
        modal
        className="p-fluid"
      >
        <div className="mb-5 pt-5">
          <Steps
            model={steps}
            activeIndex={activeIndex}
            onSelect={(e) => setActiveIndex(e.index)}
            readOnly={false}
            className="mb-3"
          />
        </div>
        
        {renderStepContent()}
      </Dialog>
    </>
  );
};